import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { APPROVALS } from "@/constants/designRequestTestIds";
import PreviewPromptModal from "@/components/PreviewPromptModal";

const TABS = [
  { id: "under_review", label: "Under Review" },
  { id: "pending_assets", label: "Pending Brand Assets" },
  { id: "approved", label: "Approved" },
];

function scoreColor(score) {
  if (score == null) return "#6B6358";
  if (score >= 90) return "#1F6B6B";
  if (score >= 70) return "#C28A2E";
  return "#B5462F";
}

export default function Approvals() {
  const { user } = useAuth();
  const [tab, setTab] = useState("under_review");
  const [rows, setRows] = useState(null);
  const [error, setError] = useState("");
  const [promptFor, setPromptFor] = useState(null); // {brand_id, design_request_id}
  const isAdmin = !!user?.is_platform_admin;

  const load = async (which = tab) => {
    setRows(null); setError("");
    try {
      const { data } = await api.get("/approvals/queue", { params: { tab: which } });
      setRows(data);
    } catch (e) {
      setError(e.response?.data?.detail || "Could not load queue");
      setRows([]);
    }
  };

  useEffect(() => { load(tab); /* eslint-disable-next-line */ }, [tab]);

  const [muted, setMuted] = useState(null);
  useEffect(() => {
    api.get("/me/notification-preferences")
      .then(({ data }) => setMuted(!!data.approval_emails_muted))
      .catch(() => setMuted(false));
  }, []);
  const toggleMute = async () => {
    const next = !muted;
    setMuted(next);
    try {
      await api.put("/me/notification-preferences", { approval_emails_muted: next });
    } catch {
      setMuted(!next);
    }
  };

  const decide = async (dvid, verb, comment) => {
    try {
      await api.post(`/document-versions/${dvid}/${verb}`, { comment: comment || null });
      await load();
    } catch (e) {
      setError(e.response?.data?.detail || `Could not ${verb}`);
    }
  };

  const decideStep = async (dvid, stepId, decision, comment) => {
    try {
      await api.post(`/document-versions/${dvid}/steps/${stepId}/decision`, {
        decision, comment: comment || null,
      });
      await load();
    } catch (e) {
      setError(e.response?.data?.detail || "Could not record decision");
    }
  };

  return (
    <div data-testid={APPROVALS.listRoot} className="max-w-6xl">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.22em] text-[#6B6358]">Review workspace</p>
          <h1 className="font-serif text-4xl tracking-tight mt-1">Approvals</h1>
        </div>
        {muted !== null && (
          <button
            type="button"
            data-testid="approvals-mute-toggle"
            onClick={toggleMute}
            title="Mute or unmute approval-workflow emails for your account"
            className={`mt-2 shrink-0 text-[10px] uppercase tracking-[0.16em] inline-flex items-center gap-2 rounded-full px-3 py-1.5 border transition-colors ${
              muted
                ? "border-[#B5462F]/40 text-[#B5462F] bg-[#B5462F]/[0.06]"
                : "border-[#1A1F2C]/20 text-[#3D3A35] hover:bg-[#1A1F2C]/[0.04]"
            }`}
          >
            {muted ? "Email notifications muted" : "Email notifications on"}
          </button>
        )}
      </div>
      <p className="text-sm text-[#3D3A35] mt-3 max-w-2xl">
        Every project moving through review. Scores reflect each project&rsquo;s
        compliance against the brand&rsquo;s active rules; rules that don&rsquo;t apply to a
        project are set aside, neither helping nor hurting the score.
      </p>

      {/* Tabs */}
      <div className="mt-8 flex gap-1 border-b border-[#1A1F2C]/10">
        {TABS.map((t) => (
          <button
            key={t.id}
            data-testid={`approvals-tab-${t.id}`}
            onClick={() => setTab(t.id)}
            className={`px-5 py-3 text-sm font-medium border-b-2 transition-colors ${
              tab === t.id
                ? "border-[#B5462F] text-[#1A1F2C]"
                : "border-transparent text-[#6B6358] hover:text-[#1A1F2C]"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {error && <p className="text-sm text-[#B5462F] mt-4">{error}</p>}

      {rows == null ? (
        <p className="text-[#6B6358] mt-8">Loading…</p>
      ) : rows.length === 0 ? (
        <div data-testid={APPROVALS.emptyState} className="mt-8 border border-dashed border-[#1A1F2C]/20 rounded-lg p-10 text-center">
          <p className="text-[#6B6358]">
            {tab === "under_review" && "Nothing waiting for review."}
            {tab === "pending_assets" && "No projects are waiting on brand assets."}
            {tab === "approved" && "No approved projects yet."}
          </p>
        </div>
      ) : (
        <div className="mt-6 overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-[0.18em] text-[#6B6358] border-b border-[#1A1F2C]/10">
                <th className="py-3 pr-4 font-medium">Date</th>
                <th className="py-3 pr-4 font-medium">Submitted by</th>
                <th className="py-3 pr-4 font-medium">Document type</th>
                <th className="py-3 pr-4 font-medium">Compliance</th>
                <th className="py-3 pr-4 font-medium">Prompt</th>
                <th className="py-3 pr-4 font-medium">Design</th>
                <th className="py-3 pr-4 font-medium text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <QueueRow
                  key={r.document_version_id}
                  r={r}
                  tab={tab}
                  isAdmin={isAdmin}
                  onPrompt={() => setPromptFor({ brand_id: r.brand_id, design_request_id: r.design_request_id })}
                  onDecide={decide}
                  onDecideStep={decideStep}
                  onReload={load}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {promptFor && isAdmin && (
        <PreviewPromptModal
          brandId={promptFor.brand_id}
          designRequestId={promptFor.design_request_id}
          onClose={() => setPromptFor(null)}
        />
      )}
    </div>
  );
}

function QueueRow({ r, tab, isAdmin, onPrompt, onDecide, onDecideStep, onReload }) {
  const [expanded, setExpanded] = useState(false);
  const date = r.project_date ? new Date(r.project_date).toLocaleDateString() : "—";
  const run = r.approval_run;
  const steps = run?.steps || [];
  const approvedCount = steps.filter((s) => s.status === "approved").length;

  return (
    <>
      <tr
        data-testid={`${APPROVALS.rowPrefix}${r.document_version_id}`}
        className="border-b border-[#1A1F2C]/[0.06] align-top"
      >
        <td className="py-3 pr-4 whitespace-nowrap text-[#3D3A35]">{date}</td>
        <td className="py-3 pr-4">
          <div className="font-medium text-[#1A1F2C]">{r.project_name}</div>
          <div className="text-xs text-[#6B6358]">{r.submitter_name}</div>
        </td>
        <td className="py-3 pr-4 text-[#3D3A35] whitespace-nowrap">{r.document_type_name}</td>
        <td className="py-3 pr-4">
          <span
            data-testid={`approvals-row-${r.document_version_id}-compliance`}
            className="inline-block px-2.5 py-0.5 rounded-full text-xs font-medium"
            style={{ backgroundColor: `${scoreColor(r.compliance_score)}1a`, color: scoreColor(r.compliance_score) }}
            title="Average pass rate across applicable brand rules"
          >
            {r.compliance_score == null ? "—" : `${r.compliance_score}`}
          </span>
        </td>
        <td className="py-3 pr-4">
          {isAdmin ? (
            <button
              type="button"
              data-testid={`approvals-row-${r.document_version_id}-prompt`}
              onClick={onPrompt}
              className="text-xs uppercase tracking-[0.14em] text-[#B5462F] hover:underline"
            >
              Preview
            </button>
          ) : (
            <span className="text-xs text-[#6B6358]">—</span>
          )}
        </td>
        <td className="py-3 pr-4">
          {r.pdf_url ? (
            <a
              href={r.pdf_url}
              target="_blank"
              rel="noreferrer"
              data-testid={`approvals-row-${r.document_version_id}-design`}
              className="text-xs uppercase tracking-[0.14em] text-[#1F6B6B] hover:underline"
            >
              View PDF
            </a>
          ) : (
            <Link
              to={r.workflow_route}
              data-testid={`approvals-row-${r.document_version_id}-design`}
              className="text-xs uppercase tracking-[0.14em] text-[#1F6B6B] hover:underline"
            >
              Open workflow
            </Link>
          )}
        </td>
        <td className="py-3 pr-0 text-right whitespace-nowrap">
          {tab === "under_review" && (
            run ? (
              <button
                data-testid={`approvals-row-${r.document_version_id}-steps-toggle`}
                onClick={() => setExpanded((v) => !v)}
                className="px-3 py-1.5 text-xs uppercase tracking-[0.16em] border border-[#1F6B6B]/50 rounded-full text-[#1F6B6B] font-medium"
              >
                {expanded ? "Hide steps" : `Review steps (${approvedCount}/${steps.length})`}
              </button>
            ) : (
              <button
                data-testid={`${APPROVALS.rowApproveButton}${r.document_version_id}`}
                onClick={() => onDecide(r.document_version_id, "approve")}
                className="px-3 py-1.5 text-xs uppercase tracking-[0.16em] bg-[#1F6B6B] text-[#F8F5EE] rounded-full font-medium"
              >
                Approve
              </button>
            )
          )}
          {tab === "pending_assets" && (
            <button
              data-testid={`approvals-row-${r.document_version_id}-resolve-toggle`}
              onClick={() => setExpanded((v) => !v)}
              className="px-3 py-1.5 text-xs uppercase tracking-[0.16em] border border-[#C28A2E]/50 rounded-full text-[#C28A2E]"
            >
              {expanded ? "Hide" : `Resolve (${r.open_blocker_count ?? 0})`}
            </button>
          )}
          {tab === "approved" && (
            <span className="text-xs uppercase tracking-[0.16em] text-[#1F6B6B]">Approved</span>
          )}
        </td>
      </tr>
      {tab === "pending_assets" && expanded && (
        <tr>
          <td colSpan={7} className="pb-4">
            <BlockerResolvePanel
              documentVersionId={r.document_version_id}
              isAdmin={isAdmin}
              onResolved={onReload}
            />
          </td>
        </tr>
      )}
      {tab === "under_review" && run && expanded && (
        <tr>
          <td colSpan={7} className="pb-4">
            <StepsPanel run={run} dvid={r.document_version_id} onDecideStep={onDecideStep} />
          </td>
        </tr>
      )}
    </>
  );
}

const STEP_STATUS = {
  pending:  { color: "#6B6358", label: "Pending" },
  approved: { color: "#1F6B6B", label: "Approved" },
  rejected: { color: "#B5462F", label: "Rejected" },
};

function StepsPanel({ run, dvid, onDecideStep }) {
  const [rejectFor, setRejectFor] = useState(null);
  const [comment, setComment] = useState("");
  return (
    <div
      data-testid={`approvals-steps-${dvid}`}
      className="bg-white border border-[#1A1F2C]/10 rounded-lg p-4 space-y-3"
    >
      <p className="text-[10px] uppercase tracking-[0.18em] text-[#6B6358]">
        Program approval workflow · {run.steps.length} step{run.steps.length === 1 ? "" : "s"}
      </p>
      {run.steps.map((s, i) => {
        const st = STEP_STATUS[s.status] || STEP_STATUS.pending;
        return (
          <div
            key={s.step_id}
            data-testid={`approvals-step-${dvid}-${s.step_id}`}
            className="border border-[#1A1F2C]/10 rounded-md p-3"
          >
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-[#1A1F2C]">
                    {i + 1}. {s.name}
                  </span>
                  {i > 0 && (
                    <span className="text-[9px] uppercase tracking-[0.14em] text-[#6B6358] border border-[#1A1F2C]/15 rounded-full px-1.5 py-0.5">
                      {s.relation_to_previous === "concurrent" ? "With previous" : "After previous"}
                    </span>
                  )}
                  {s.is_active && s.status === "pending" && (
                    <span className="text-[9px] uppercase tracking-[0.14em] text-[#C28A2E]">Active</span>
                  )}
                </div>
                <p className="text-[11px] text-[#6B6358] mt-0.5 truncate">
                  Approvers: {s.approvers.map((a) => a.name).join(", ") || "—"}
                </p>
                {s.comment && <p className="text-[11px] text-[#3D3A35] mt-0.5 italic">“{s.comment}”</p>}
              </div>
              <span
                className="shrink-0 text-[10px] uppercase tracking-[0.14em] px-2 py-0.5 rounded-full"
                style={{ backgroundColor: `${st.color}1a`, color: st.color }}
              >
                {st.label}{s.decided_by_name ? ` · ${s.decided_by_name}` : ""}
              </span>
            </div>
            {s.can_current_user_decide && (
              <div className="mt-2">
                {rejectFor === s.step_id ? (
                  <div className="flex items-center gap-2">
                    <input
                      data-testid={`approvals-step-${dvid}-${s.step_id}-reject-comment`}
                      value={comment}
                      onChange={(e) => setComment(e.target.value)}
                      placeholder="Reason for rejection…"
                      className="flex-1 text-sm px-3 py-1.5 border border-[#1A1F2C]/20 rounded-md bg-[#F8F5EE]"
                    />
                    <button
                      data-testid={`approvals-step-${dvid}-${s.step_id}-reject-confirm`}
                      onClick={() => { onDecideStep(dvid, s.step_id, "reject", comment); setRejectFor(null); setComment(""); }}
                      className="px-3 py-1.5 text-xs uppercase tracking-[0.16em] bg-[#B5462F] text-[#F8F5EE] rounded-full"
                    >
                      Confirm reject
                    </button>
                    <button onClick={() => { setRejectFor(null); setComment(""); }} className="text-xs text-[#6B6358]">Cancel</button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <button
                      data-testid={`approvals-step-${dvid}-${s.step_id}-approve`}
                      onClick={() => onDecideStep(dvid, s.step_id, "approve")}
                      className="px-3 py-1.5 text-xs uppercase tracking-[0.16em] bg-[#1F6B6B] text-[#F8F5EE] rounded-full"
                    >
                      Approve step
                    </button>
                    <button
                      data-testid={`approvals-step-${dvid}-${s.step_id}-reject`}
                      onClick={() => setRejectFor(s.step_id)}
                      className="px-3 py-1.5 text-xs uppercase tracking-[0.16em] border border-[#B5462F]/50 text-[#B5462F] rounded-full"
                    >
                      Reject
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function BlockerResolvePanel({ documentVersionId, isAdmin, onResolved }) {
  const [blockers, setBlockers] = useState(null);
  const [error, setError] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get(`/document-versions/${documentVersionId}/blockers`);
      setBlockers(data.filter((b) => b.status === "open"));
    } catch (e) {
      setError(e.response?.data?.detail || "Could not load blockers");
      setBlockers([]);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [documentVersionId]);

  const resolve = async (blockerId, note) => {
    try {
      await api.post(`/blockers/${blockerId}/resolve`, { resolution_note: note || null });
      await load();
      onResolved?.();
    } catch (e) {
      setError(e.response?.data?.detail || "Could not resolve");
    }
  };

  return (
    <div className="bg-[#1A1F2C]/[0.03] border border-[#1A1F2C]/10 rounded-lg p-4">
      <p className="text-[10px] uppercase tracking-[0.18em] text-[#6B6358] mb-2">Open brand-asset blockers</p>
      {error && <p className="text-sm text-[#B5462F]">{error}</p>}
      {blockers == null ? (
        <p className="text-sm text-[#6B6358]">Loading…</p>
      ) : blockers.length === 0 ? (
        <p className="text-sm text-[#6B6358]">No open blockers remain.</p>
      ) : (
        <div className="space-y-2">
          {blockers.map((b) => (
            <BlockerResolveRow key={b.blocker_id} b={b} isAdmin={isAdmin} onResolve={resolve} />
          ))}
        </div>
      )}
    </div>
  );
}

function BlockerResolveRow({ b, isAdmin, onResolve }) {
  const [note, setNote] = useState("");
  return (
    <div className="bg-white border border-[#1A1F2C]/10 rounded-md p-3">
      <div className="flex flex-wrap gap-3 justify-between items-baseline">
        <div className="min-w-[240px]">
          <div className="text-[10px] uppercase tracking-[0.18em] text-[#C28A2E]">
            {b.kind} · {b.panel_id} / {b.slot_id}
          </div>
          <div className="text-sm text-[#1A1F2C] font-medium mt-0.5">
            {b.slot_label || b.slot_id}
            {b.subject_hint && <span className="font-normal text-[#6B6358]"> · {b.subject_hint}</span>}
          </div>
          {b.what_the_ai_tried && (
            <p className="text-xs text-[#6B6358] mt-1 italic">{b.what_the_ai_tried}</p>
          )}
        </div>
      </div>
      {isAdmin && (
        <div className="mt-2 flex gap-2 items-center">
          <input
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Resolution note (optional)"
            data-testid={`approvals-blocker-${b.blocker_id}-note`}
            className="flex-1 px-3 py-1.5 border border-[#1A1F2C]/20 rounded-md text-sm bg-[#F8F5EE]"
          />
          <button
            data-testid={`approvals-blocker-${b.blocker_id}-resolve`}
            onClick={() => onResolve(b.blocker_id, note.trim())}
            className="px-4 py-1.5 bg-[#1F6B6B] text-[#F8F5EE] rounded-full text-xs uppercase tracking-[0.16em] font-medium"
          >
            Mark resolved
          </button>
        </div>
      )}
    </div>
  );
}
